import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getExplorerTxUrl, CHAIN_NAME } from '@/utils/chain';
import {
  Plus,
  Activity,
  Upload,
  Shield,
  ShieldCheck,
  Loader2,
  ExternalLink,
  FileText,
  UserCheck,
  Clock,
  X,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';
import { FileUploadModal } from './FileUploadModal';
import { AddMedicalUpdateModal } from './AddMedicalUpdateModal';
import { DocumentViewerModal } from './DocumentViewerModal';
import { AssignProfessionalModal } from '../AssignProfessionalModal';
import {
  CreateMedicalUpdateRequest,
  MedicalUpdateType,
  MedicalUpdateStatus,
  MedicalUpdate,
} from '../../../../api/pets';
import {
  useMedicalUpdates,
  useCreateMedicalUpdate,
  useUpdateMedicalUpdate,
} from '../../../../hooks/usePetRecords';
import { apiClient } from '@/api/axios';
import { parseUploadInitResponse, getAbsoluteUploadUrl } from '@/api/documents';
import { useNotifications } from '../../context/NotificationsContext';
import { usePets } from '../../context/PetsContext';

interface MedicalUpdatesProps {
  petId: string;
}

export function MedicalUpdates({ petId }: MedicalUpdatesProps) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<string | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingUpdate, setViewingUpdate] = useState<MedicalUpdate | null>(
    null
  );
  const [showAssignProfessional, setShowAssignProfessional] = useState(false);
  const [verifyingRecordId, setVerifyingRecordId] = useState<string | null>(null);
  const [verificationRequested, setVerificationRequested] = useState<Set<string>>(new Set());
  const [petCertificates, setPetCertificates] = useState<any[]>([]);
  const [viewingCert, setViewingCert] = useState<any>(null);

  // Fetch certificates for this pet to link with verified medical updates
  useEffect(() => {
    apiClient.get(`/pets/${petId}/certificates`).then(({ data }) => {
      setPetCertificates(data.certificates || []);
    }).catch(() => {});
  }, [petId]);

  const {
    data: updates = [],
    isLoading,
    error,
    isFetching,
  } = useMedicalUpdates(petId);
  const createUpdateMutation = useCreateMedicalUpdate(petId);
  const updateMedicalUpdateMutation = useUpdateMedicalUpdate(petId);
  const { addNotification } = useNotifications();
  const { pets } = usePets();
  const petName = pets.find((p) => p.id === petId)?.name ?? 'Your pet';

  const handleRequestVerification = (recordId: string) => {
    setVerifyingRecordId(recordId);
    setShowAssignProfessional(true);
  };

  const handleProfessionalAssigned = async (professional: { id: string; name: string; type: string; source: 'existing' | 'invited'; email?: string | null; phone?: string | null }) => {
    if (!verifyingRecordId) return;
    try {
      await apiClient.post(`/pets/${petId}/request-verification`, {
        recordId: verifyingRecordId,
        recordTable: 'medical_updates',
        professionalId: professional.id || undefined,
        professionalName: professional.name,
        professionalEmail: professional.email || undefined,
        professionalType: professional.type || 'veterinary',
      });
      setVerificationRequested((prev) => new Set(prev).add(verifyingRecordId));
      addNotification({
        type: 'document_shared',
        title: 'Verification Requested',
        message: `Verification request sent to ${professional.name}`,
      });
    } catch {
      addNotification({
        type: 'edit_requested',
        title: 'Request Failed',
        message: 'Could not send verification request. Please try again.',
      });
    }
    setShowAssignProfessional(false);
    setVerifyingRecordId(null);
  };

  const handleAddUpdate = async (updateData: {
    type: string;
    title: string;
    date: string;
    veterinarian: string;
    clinicName: string;
    description: string;
    medications?: string[];
    status: string;
    followUpDate?: string;
  }) => {
    try {
      const request: CreateMedicalUpdateRequest = {
        updateType: updateData.type as MedicalUpdateType,
        title: updateData.title,
        date: updateData.date,
        veterinarian: updateData.veterinarian,
        clinicName: updateData.clinicName,
        description: updateData.description,
        medications: updateData.medications,
        status: updateData.status as MedicalUpdateStatus,
        followUpDate: updateData.followUpDate || undefined,
        verified: false,
      };

      await createUpdateMutation.mutateAsync(request);
      addNotification({
        type: 'record_added',
        title: 'Health record added',
        message: `Medical update "${updateData.title}" was added for ${petName}.`,
        petId,
        petName,
        icon: '🏥',
        source: 'E-Passport',
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to create medical update:', err);
      alert(
        t(
          'features.healthRecords.errors.addMedicalUpdateFailed',
          'Aggiunta aggiornamento medico non riuscita. Riprova.'
        )
      );
    }
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      Diagnosis: '🔍',
      Treatment: '💊',
      Prescription: '📋',
      Recovery: '❤️‍🩹',
    };
    return icons[type] || '🏥';
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Active:
        'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      Resolved:
        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      Ongoing:
        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm capitalize ${styles[status] || ''}`}
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {status}
      </span>
    );
  };

  // Only show loading spinner on initial load (no cached data)
  if (isLoading && updates.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error && updates.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4">
        <p
          className="text-red-700 dark:text-red-300"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Failed to load medical updates
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('features.healthRecords.medicalUpdatesTitle')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.medicalUpdatesDesc')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isFetching && (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          )}
          <button
            onClick={() => setShowAddModal(true)}
            disabled={createUpdateMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {createUpdateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {t('features.healthRecords.addUpdate')}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {updates.length === 0 && (
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/8 rounded-lg p-8 text-center">
          <Activity className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-4" />
          <p
            className="text-gray-600 dark:text-white/70 mb-2"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {t('features.healthRecords.noMedicalUpdatesYet')}
          </p>
          <p
            className="text-sm text-gray-500 dark:text-white/50"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.addFirstMedicalUpdate')}
          </p>
        </div>
      )}

      {/* Active Treatments */}
      {updates.filter((u) => u.status === 'Active' || u.status === 'Ongoing')
        .length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h4
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              Active Treatments
            </h4>
          </div>
          <div className="space-y-2">
            {updates
              .filter((u) => u.status === 'Active' || u.status === 'Ongoing')
              .map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center gap-2 text-gray-900 dark:text-white">
                    <span>{getTypeIcon(u.updateType)}</span>
                    <span>{u.title}</span>
                  </span>
                  {getStatusBadge(u.status)}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Medical Updates List */}
      <div className="space-y-4">
        {updates.map((update) => (
          <div
            key={update.id}
            className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{getTypeIcon(update.updateType)}</div>
                <div>
                  <h4
                    className="mb-1 text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {update.title}
                  </h4>
                  <div
                    className="text-sm text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {new Date(update.date).toLocaleDateString()} •{' '}
                    {update.veterinarian} • {update.clinicName}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {getStatusBadge(update.status)}
                {update.verified && (
                  <span
                    className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <Shield className="w-3 h-3" />
                    Vet Verified
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Description
              </div>
              <div
                className="text-sm text-gray-900 dark:text-white/90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {update.description}
              </div>
            </div>

            {update.medications && update.medications.length > 0 && (
              <div className="mb-4">
                <div
                  className="text-sm text-gray-600 dark:text-white/50 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Medications
                </div>
                <div className="space-y-1">
                  {update.medications.map((med, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full" />
                      <span className="text-gray-900 dark:text-white/90">
                        {med}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {update.followUpDate && (
              <div className="mb-4">
                <div
                  className="text-sm text-gray-600 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Follow-up Date
                </div>
                <div
                  className="text-sm text-gray-900 dark:text-white/90"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {new Date(update.followUpDate).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* Blockchain Status */}
            <div className="mb-4">
              {update.txSignature ? (
                <a
                  href={getExplorerTxUrl(update.txSignature)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Verified on {CHAIN_NAME}
                  <span className="font-mono text-gray-500 dark:text-white/50">
                    · {update.txSignature.substring(0, 8)}…{update.txSignature.substring(update.txSignature.length - 4)}
                  </span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                  Awaiting blockchain sync
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedUpdate(update.id);
                  setShowFileUpload(true);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <Upload className="w-4 h-4" />
                Attach Documents
              </button>
              {(() => {
                // Find matching certificate for this update
                const matchingCert = update.verified ? petCertificates.find((c: any) =>
                  c.title === update.title || c.petName === petName
                ) : null;
                const hasDocument = !!update.documentHash;
                const hasCert = !!matchingCert;
                return (
                  <button
                    onClick={() => {
                      if (hasCert) {
                        setViewingCert(matchingCert);
                      } else if (hasDocument) {
                        setViewingUpdate(update);
                        setShowDocumentViewer(true);
                      }
                    }}
                    disabled={!hasDocument && !hasCert}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded transition-colors ${
                      hasDocument || hasCert
                        ? 'border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'border-gray-200 dark:border-white/10 text-gray-400 dark:text-white/30 cursor-not-allowed'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {hasCert ? <ShieldCheck className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    {hasCert ? 'View Certificate' : 'View Document'}
                  </button>
                );
              })()}
              {!update.verified && !verificationRequested.has(update.id) && (
                <button
                  onClick={() => handleRequestVerification(update.id)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border-2 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <UserCheck className="w-4 h-4" />
                  Request Verification
                </button>
              )}
              {!update.verified && verificationRequested.has(update.id) && (
                <span
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-yellow-400 dark:border-yellow-500/50 text-yellow-600 dark:text-yellow-400 rounded bg-yellow-50 dark:bg-yellow-900/10"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <Clock className="w-4 h-4" />
                  Verification Pending
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Smart Contract Info */}
      {updates.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h4
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              Mutable Smart Contract
            </h4>
          </div>
          <p
            className="text-sm text-gray-700 dark:text-purple-100 mb-3"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Medical updates are written to a mutable smart contract linked to
            your pet&apos;s DID. Each update appends to the history - nothing is
            overwritten. This creates a complete, auditable medical timeline.
          </p>
          <div className="flex items-center gap-4 text-xs">
            <div>
              <span
                className="text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Total Updates:
              </span>{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {updates.length}
              </span>
            </div>
            <div>
              <span
                className="text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Verified:
              </span>{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {updates.filter((u) => u.verified).length}
              </span>
            </div>
            <div>
              <span
                className="text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                On-Chain:
              </span>{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {updates.filter((u) => u.txSignature).length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
        <h4
          className="mb-2 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
        >
          {t('features.healthRecords.whyMedicalUpdatesMatter', {
            defaultValue: 'Why medical updates matter',
          })}
        </h4>
        <p
          className="text-sm text-gray-700 dark:text-blue-100"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('features.healthRecords.whyMedicalUpdatesBody', {
            defaultValue:
              'A continuous medical timeline helps track diagnoses, treatments, and recovery progress across visits and providers.',
          })}
        </p>
      </div>

      {showFileUpload && selectedUpdate && (
        <FileUploadModal
          onClose={() => {
            setShowFileUpload(false);
            setSelectedUpdate(null);
          }}
          category="Lab Results"
          onUpload={async (fileData) => {
            if (!selectedUpdate) return;
            const contentType =
              fileData.file.type || 'application/octet-stream';
            const initRes = await apiClient.post('/documents/upload-init', {
              filename: fileData.file.name,
              contentType,
              folder: 'medical-documents',
              ttlSeconds: 900,
            });
            const raw = initRes.data as
              | {
                  uploadUrl?: string;
                  url?: string;
                  key?: string;
                  body?: unknown;
                  data?: {
                    uploadUrl?: string;
                    url?: string;
                    key?: string;
                  };
                }
              | undefined;
            const body =
              typeof raw?.body === 'string'
                ? (() => {
                    try {
                      return JSON.parse(raw.body) as Record<string, unknown>;
                    } catch {
                      return null;
                    }
                  })()
                : raw?.body && typeof raw.body === 'object'
                  ? (raw.body as Record<string, unknown>)
                  : null;
            const nested =
              raw?.data && typeof raw.data === 'object' ? raw.data : null;
            const uploadUrl =
              (typeof raw?.uploadUrl === 'string' && raw.uploadUrl) ||
              (typeof raw?.url === 'string' && raw.url) ||
              (typeof nested?.uploadUrl === 'string' && nested.uploadUrl) ||
              (typeof nested?.url === 'string' && nested.url) ||
              (body && typeof body.uploadUrl === 'string'
                ? body.uploadUrl
                : '') ||
              (body && typeof body.url === 'string' ? body.url : '');
            const key =
              (typeof raw?.key === 'string' && raw.key) ||
              (typeof nested?.key === 'string' && nested.key) ||
              (body && typeof body.key === 'string' ? body.key : '');

            if (!uploadUrl || !key) {
              throw new Error('Upload init did not return a valid upload URL');
            }

            if (/^https?:\/\//i.test(uploadUrl)) {
              const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                body: fileData.file,
              });
              if (!putRes.ok) {
                throw new Error(`Upload failed with status ${putRes.status}`);
              }
            } else {
              const relativeUrl = uploadUrl.startsWith('/')
                ? uploadUrl
                : `/${uploadUrl}`;
              try {
                await apiClient.put(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              } catch (err) {
                const status = (err as { response?: { status?: number } })
                  ?.response?.status;
                if (status !== 405) throw err;
                await apiClient.post(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              }
            }
            await updateMedicalUpdateMutation.mutateAsync({
              medicalUpdateId: selectedUpdate,
              data: { documentHash: key },
            });
          }}
        />
      )}

      {showDocumentViewer && viewingUpdate?.documentHash && (
        <DocumentViewerModal
          onClose={() => {
            setShowDocumentViewer(false);
            setViewingUpdate(null);
          }}
          documentHash={viewingUpdate.documentHash}
          documentTitle={viewingUpdate.title}
        />
      )}

      {showAddModal && (
        <AddMedicalUpdateModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddUpdate}
        />
      )}

      {showAssignProfessional && (
        <AssignProfessionalModal
          isOpen={showAssignProfessional}
          onClose={() => {
            setShowAssignProfessional(false);
            setVerifyingRecordId(null);
          }}
          professionalType="veterinary"
          professionalLabel="Veterinarian"
          petId={petId}
          onAssign={handleProfessionalAssigned}
        />
      )}
      {/* Certificate Viewer Modal */}
      {viewingCert && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={(e) => { if (e.target === e.currentTarget) setViewingCert(null); }}>
          <div className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl" style={{ maxWidth: '28rem', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'linear-gradient(to right, #059669, #047857)', color: 'white', padding: '1.5rem', borderRadius: '1rem 1rem 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck style={{ width: '20px', height: '20px' }} />
                  {viewingCert.title || 'Certificate'}
                </h2>
                <button type="button" onClick={() => setViewingCert(null)} style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', background: 'none', border: 'none' }}>
                  <X style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{viewingCert.certificateNumber}</p>
            </div>
            <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {/* Visual Certificate Card */}
              <div style={{ width: '100%', background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                <div style={{ background: '#0f172a', padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase' }}>Issued by</p>
                    <p style={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>{viewingCert.issuedByTitle ? `${viewingCert.issuedByTitle} ` : ''}{viewingCert.issuedByName || 'Professional'}</p>
                    {viewingCert.businessName && <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{viewingCert.businessName}</p>}
                  </div>
                  <ShieldCheck style={{ color: '#34d399', width: '22px', height: '22px' }} />
                </div>
                <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Issued To</p>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{viewingCert.petName}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Document</p>
                    <p style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a' }}>{viewingCert.title}</p>
                  </div>
                </div>
                {viewingCert.data && (() => {
                  const d = typeof viewingCert.data === 'string' ? (() => { try { return JSON.parse(viewingCert.data); } catch { return {}; } })() : viewingCert.data;
                  return (
                    <div style={{ padding: '12px 16px' }}>
                      {Object.entries(d).filter(([k, v]) => v && !['certificateId','certificateNumber','certificateType','title','issuedAt','issuedByName','issuedByTitle','documentHash','systems'].includes(k)).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', borderBottom: '1px solid #f1f5f9', padding: '4px 0' }}>
                          <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</span>
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', fontSize: '11px' }}>
                  {viewingCert.certificateNumber && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Certificate No.</span><span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', fontSize: '10px' }}>{viewingCert.certificateNumber}</span></div>}
                  {viewingCert.issuedAt && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Issued</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{new Date(viewingCert.issuedAt).toLocaleDateString()}</span></div>}
                  {viewingCert.validFrom && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid From</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingCert.validFrom}</span></div>}
                  {viewingCert.validUntil && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid Until</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingCert.validUntil}</span></div>}
                </div>
                <div style={{ background: '#ecfdf5', padding: '12px 16px', borderTop: '1px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '8px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '3px' }}><ShieldCheck style={{ width: '10px', height: '10px' }} /> Cryptographically Verified</p>
                    <p style={{ fontSize: '7px', color: '#047857', fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>{viewingCert.documentHash ? String(viewingCert.documentHash).slice(0, 32) + '...' : viewingCert.certificateNumber}</p>
                  </div>
                  <div style={{ background: '#fff', padding: '4px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                    <QRCodeCanvas value={viewingCert.verificationUrl || `https://api-dev.petso.dev/verify/${viewingCert.certificateNumber}`} size={48} />
                  </div>
                </div>
              </div>
              {viewingCert.verificationUrl && (
                <a href={viewingCert.verificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mt-3">
                  <ExternalLink className="w-4 h-4" />
                  Verify certificate publicly
                </a>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button type="button" onClick={() => setViewingCert(null)} style={{ width: '100%', padding: '10px', backgroundColor: '#059669', color: '#fff', borderRadius: '8px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
